/*******************************************************
 * Copyright (C) 2010-Present Avant Assessment
 * All Rights Reserved
 *******************************************************/

import {
    Grid,
} from "@material-ui/core"
import * as React from "react"
import { useEffect, useState } from "react"
import {H3Text} from "../../../../styles/AvantTypography"
import { LoginDTO } from "../../../../types/types"
import { TimeUtil } from "../../../../util/TimeUtil"
import { AvantExpansionPanel } from "../../../generic-components/AvantExpansionPanel"
import { RosterDialog } from "./rostering/RosterDialog"
import { EmailRemindersDialog } from "./EmailRemindersDialog"
import { productStore } from "../../../common/products/ProductStore"
import { contentAreaStore } from "../../../common/contentArea/ContentAreaStore";
import {TeacherPermissions} from "../../../../components/admin-portal/DevTools/LoginTool";
import {SchedulingLink} from "../../common/components/SchedulingLink";
import {RegistrationLink} from "../../common/components/RegistrationLink";
import {EmailReminders} from "../../common/components/EmailReminders";
import {RosterTestTakers} from "../../common/components/RosterTestTakers";
import {buildProctoredTestTakerRegistrationLink, isSecureURLForWebAndLocal} from "../../../../util/UrlUtil";
import {groupMasterStore} from "../../../common/group/GroupMasterStore";
import {PendingProctorSetup} from "./PendingProctorSetup"
import {ProctorTypes} from "../../../../util/Constants";

/**
 * @return `true` if the login is unexpired, or if there is no expiration date.
 *
 * `false` otherwise.
 */
function loginNotExpired(login: LoginDTO): boolean {
    const expirationDate = login.loginExpires ? TimeUtil.formatMilliToMonthDayYearLong(Number(login.loginExpires)) : null
    return expirationDate === null || new Date(expirationDate) > new Date() // login has not expired -- null means forever
}

/**
 * @return `true` if the password date is unexpired, or if there is no expiration date.
 *
 * `false` otherwise.
 */
function passwordNotExpired(login: LoginDTO): boolean {
    const passwordExpirationDate = login.passwordExpires ? TimeUtil.formatMilliToMonthDayYearLong(Number(login.passwordExpires)) : null
    return passwordExpirationDate === null || new Date(passwordExpirationDate) > new Date() // password has not expired -- null means forever
}

function loginActive(login: LoginDTO): boolean {
    return (login.id !== null && login.id !== undefined) && (login.active === true)
}

function isTeacher(username: string): boolean {
    return username.endsWith("-t") ?? false
}

/** A filtering function to prevent a teacher from viewing rostered test takers not associated with their teacher code.
 *
 *  If the currentUser is not a teacher then they are a district, school, or other admin user.
 *  There is no singular test taker associated with them, so don't filter and simply return true.
 */
function matchesTeacher(login: LoginDTO) {
    const currentUserName = productStore.loginProduct?.userName ?? ""
    if (isTeacher(currentUserName)) {
        return currentUserName.includes(login.username ?? "")
    }
    // Not a teacher code. We are not restricted to showing only one associated test taker code. Simply return true.
    return true
}

function permissionsToRoster() {
    return productStore.loginProduct?.permissions?.includes(TeacherPermissions.ROSTER.valueOf()) ?? false
}

function isRostered(login: LoginDTO) {
    return login.rostered === true
}

/** Currently the only supported `ProctorType` is `P360`
 *  @return `true` if the test code is a `P360` proctored login, `false` otherwise.
 * .*/
function isProctored(login: LoginDTO) {
    return login.proctorType === ProctorTypes.P360.valueOf()
}

export interface TestControlsProps {
    /** Callback used after finishing an action that may have updated backend state.
     *  In practice, a user action in a dialog such as `RosterDialog` may update backend state.
     *  We reload the new state when we close the dialog.
     *
     *  Note: This value is nullable because these dialogs are not loaded for `PLACEReportPage`, nor are they currently supported by `PLACEReportPage`.
     * */
    reloadReport?: () => void,
}

export function TestControls({...props}: TestControlsProps) {
    const [rosteredOrProctoredLogins, setRosteredOrProctoredLogins] = useState<LoginDTO[]>([])
    const [rosterDialogOpen, setRosterDialogOpen] = useState(false)
    const [emailRemindersDialogOpen, setEmailRemindersDialogOpen] = useState(false)
    const [selectedLogin, setSelectedLogin] = useState<LoginDTO | undefined>(undefined)
    const groupInfo = groupMasterStore.groupInfo

    const handleRosterStudents = (login: LoginDTO | undefined) => {
        setSelectedLogin(login)
        setRosterDialogOpen(true)
    }

    const handleRosterClose = () => {
        setRosterDialogOpen(false)
        props.reloadReport?.()
    }

    function handleEmailReminders(login: LoginDTO | undefined) {
        setSelectedLogin(login)
        setEmailRemindersDialogOpen(true)
    }

    const handleEmailRemindersClose = () => {
        setEmailRemindersDialogOpen(false)
        props.reloadReport?.()
    }

    useEffect(() => {

        // Get every login associated with the login group, and then filter to include only rostered logins that meet certain criteria.
        const allLoginsInTheGroup = groupInfo ? groupInfo.loginList : []
        const rosteredOrProctoredLogins: LoginDTO[] = allLoginsInTheGroup.filter((login: LoginDTO) => {
            return loginNotExpired(login) &&
            passwordNotExpired(login) &&
            loginActive(login) &&
                (isRostered(login) || isProctored(login)) &&
                matchesTeacher(login)
        }).sort((a: LoginDTO, b: LoginDTO) => {
            const aSortBy = (a.username) ? a.username.toLocaleUpperCase() : ""
            const bSortBy = (b.username) ? b.username.toLocaleUpperCase() : ""
            return aSortBy.localeCompare(bSortBy)
        })
        setRosteredOrProctoredLogins([...rosteredOrProctoredLogins])
    }, [])

    return (
        <>
            {(rosteredOrProctoredLogins.length > 0) && (
                <>
                    <AvantExpansionPanel title={"Test Controls"} >
                        <Grid container style={{padding: "20px", gap: "2rem"}}>
                            {rosteredOrProctoredLogins.map((login) => (
                                <Grid container key={login.id}
                                      style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "2rem",
                                      }}
                                >
                                    <H3Text>
                                        {login.username}
                                        {(login.contentAreaId && contentAreaStore.convertContentAreaIdToLanguageName(login.contentAreaId)) ? ` (${contentAreaStore.convertContentAreaIdToLanguageName(login.contentAreaId)})` : 'content area not found'}
                                    </H3Text>
                                    {props.reloadReport &&
                                        <Grid style={{display: "flex", gap: "2rem", alignItems: "center"}}>
                                            {
                                                isRostered(login) &&
                                                permissionsToRoster() &&
                                                <RosterTestTakers login={login}
                                                                  handleRosterStudents={handleRosterStudents}/>
                                            }
                                            {
                                                isProctored(login) &&
                                                login.anyProctorScheduleIsAttached &&
                                                isSecureURLForWebAndLocal(login.schedulingLink ?? "") &&
                                                <EmailReminders login={login}
                                                                handleEmailReminders={handleEmailReminders}/>
                                            }
                                        </Grid>
                                    }
                                    <Grid style={{display: "flex", gap: "2rem", alignItems: "center"}}>
                                        {
                                            isProctored(login) &&
                                            isRostered(login) &&
                                            isSecureURLForWebAndLocal(buildProctoredTestTakerRegistrationLink(login.id)) &&
                                            <RegistrationLink
                                                url={buildProctoredTestTakerRegistrationLink(login.id)}/>
                                        }
                                        {
                                            isProctored(login) &&
                                            login.anyProctorScheduleIsAttached &&
                                            isSecureURLForWebAndLocal(login.schedulingLink ?? "") &&
                                            <SchedulingLink url={login.schedulingLink ?? ""}/>
                                        }
                                        {
                                            isProctored(login) &&
                                            !login.anyProctorScheduleIsAttached &&
                                            <PendingProctorSetup/>
                                        }
                                    </Grid>
                                </Grid>
                            ))}
                        </Grid>
                    </AvantExpansionPanel>
                    <RosterDialog
                        login={selectedLogin}
                        open={rosterDialogOpen}
                        handleRosterClose={handleRosterClose}
                    />
                    <EmailRemindersDialog
                        login={selectedLogin}
                        open={emailRemindersDialogOpen}
                        handleEmailRemindersClose={handleEmailRemindersClose}
                    />
                </>
            )}
        </>
    )
}
